/**
 * HTML sanitizer + URL guard for CMS-authored content rendered via
 * `dangerouslySetInnerHTML`.
 *
 * `sanitizeHtml` is a parser-based ALLOWLIST sanitizer built on the
 * `xss` library (js-xss) — NOT regex. This matters because the sinks
 * run isomorphically (SSR + client hydration) and the app's CSP uses
 * `script-src 'unsafe-inline'`, so CSP does not backstop these sinks:
 * the sanitizer is the primary XSS control. A real HTML parser closes
 * the bypasses a regex denylist cannot (e.g. `<img/onerror=…>` with a
 * slash separator, entity-encoded `jav&#x09;ascript:` schemes, and
 * malformed/mutated tags).
 *
 * The URL helpers below (`isSafeUrl` / `safeUrl` / `isSafeImageUrl` /
 * `safeImageUrl`) are independent scheme guards used directly in JSX.
 */
import { filterXSS, type IFilterXSSOptions } from 'xss';

/**
 * URL schemes that are SAFE to use in href/src attributes.
 * Everything else (including `javascript:`, `data:`, `vbscript:`,
 * `file:`, `blob:`) is replaced with `#`.
 *
 * Note: `mailto:` and `tel:` are intentionally allowed — they're
 * harmless and commonly needed in content.
 */
const SAFE_URL_SCHEMES = new Set([
  'http:',
  'https:',
  'mailto:',
  'tel:',
]);

/**
 * Returns true when `url` is safe to use as an `href` or `src`.
 * Relative URLs (start with `/`, `./`, `../`, `#`, or `?`) are
 * always safe. Bare schemes (e.g. `javascript:`) are blocked.
 */
export function isSafeUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const trimmed = String(url).trim();
  if (!trimmed) return false;
  // Relative or anchor — safe.
  if (/^[/#?]/.test(trimmed) || /^\.\.?\//.test(trimmed)) return true;
  // Has a scheme — must be in allowlist.
  const colon = trimmed.indexOf(':');
  if (colon === -1) {
    // No scheme, not relative → treat as relative-style safe link.
    return true;
  }
  const scheme = trimmed.slice(0, colon + 1).toLowerCase();
  return SAFE_URL_SCHEMES.has(scheme);
}

/**
 * Returns the URL when safe, otherwise `'#'`. Convenience wrapper
 * around {@link isSafeUrl} for the common "use this or fallback"
 * pattern in JSX/template code.
 */
export function safeUrl(url: string | null | undefined): string {
  return isSafeUrl(url) ? String(url).trim() : '#';
}

/**
 * Returns true when `url` is safe to use as an image `src`.
 * Stricter than {@link isSafeUrl}: blocks `data:` URLs (which can
 * smuggle SVG-with-script) and limits to http/https/relative.
 */
export function isSafeImageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const trimmed = String(url).trim();
  if (!trimmed) return false;
  if (/^[/#?]/.test(trimmed) || /^\.\.?\//.test(trimmed)) return true;
  const colon = trimmed.indexOf(':');
  if (colon === -1) return true;
  const scheme = trimmed.slice(0, colon + 1).toLowerCase();
  return scheme === 'http:' || scheme === 'https:';
}

/**
 * Returns the image src when safe, otherwise empty string (so the
 * `<img>` renders broken instead of executing a payload).
 */
export function safeImageUrl(url: string | null | undefined): string {
  return isSafeImageUrl(url) ? String(url).trim() : '';
}

// ─── HTML sanitizer (allowlist, parser-based via js-xss) ───────────

// Common global attributes safe on most flow/inline elements. `style`
// is included because js-xss runs it through its CSS filter (strips
// `expression()`, `url(javascript:)`, `behavior`, …). `class`/`id`
// carry the CMS/theme styling. Note: `on*` handlers are NOT here, so
// they are stripped from every tag.
const G = ['class', 'id', 'style', 'dir', 'title', 'lang'];

/**
 * Allowlist of tag → permitted attributes for CMS-authored HTML
 * (Tiptap output: headings, lists, tables, links, images, inline
 * marks, code/quotes). Any tag NOT listed has its markup stripped
 * (inner text kept); script/style additionally have their CONTENT
 * removed. `iframe`/`object`/`embed`/`svg`/`form`/`link`/`meta` are
 * intentionally absent → stripped (parity with the old denylist).
 */
const HTML_WHITELIST: NonNullable<IFilterXSSOptions['whiteList']> = {
  a: ['href', 'title', 'target', 'rel', 'name', ...G],
  p: G,
  br: [],
  hr: [],
  span: G,
  div: G,
  h1: G, h2: G, h3: G, h4: G, h5: G, h6: G,
  strong: G, b: G, em: G, i: G, u: G, s: G, strike: G, del: G, ins: G,
  mark: G, small: G, sub: G, sup: G, abbr: G,
  code: G, pre: G, kbd: G, samp: G, var: G,
  blockquote: [...G, 'cite'],
  ul: G,
  ol: [...G, 'start', 'type', 'reversed'],
  li: [...G, 'value'],
  dl: G, dt: G, dd: G,
  img: [...G, 'src', 'alt', 'width', 'height', 'loading', 'srcset', 'sizes'],
  figure: G, figcaption: G,
  table: G, thead: G, tbody: G, tfoot: G, caption: G,
  colgroup: [...G, 'span'], col: [...G, 'span'],
  tr: G,
  td: [...G, 'colspan', 'rowspan', 'headers'],
  th: [...G, 'colspan', 'rowspan', 'scope', 'headers'],
};

const XSS_OPTIONS: IFilterXSSOptions = {
  whiteList: HTML_WHITELIST,
  // Unknown tag → drop the tag markup but keep its text content …
  stripIgnoreTag: true,
  // … except <script>/<style>, whose CONTENT is removed entirely.
  stripIgnoreTagBody: ['script', 'style'],
  // js-xss's default `safeAttrValue` neutralizes javascript:/vbscript:
  // in href/src and filters style via its CSS allowlist; disallowed
  // attributes (incl. every on* handler) are dropped by default.
};

/**
 * Sanitize CMS/user HTML for `dangerouslySetInnerHTML` using an
 * allowlist parser (js-xss). Safe to run both server- and client-side
 * (the render sinks are isomorphic). Do NOT skip it because you
 * "trust the source": a backend/content compromise otherwise becomes
 * site-wide XSS.
 *
 * Returns `''` for `null`/`undefined`/non-string input.
 */
export function sanitizeHtml(input: unknown): string {
  if (typeof input !== 'string' || input.length === 0) return '';
  return filterXSS(input, XSS_OPTIONS);
}
